/**
 * Ce que `<ChampionFigure>` montre d'un champion : une pose fixe, ou sa danse. Pur.
 *
 * La danse est celle de l'ancien écran de fin d'activité (`ChampionImageM congratMode`) : une de ses
 * animations de félicitations (`assets/animations/<nom>/<nom>-congratN.webp`), tirée au hasard une
 * fois. Les listes sont celles de `ChampionImageM`, que la marque tient : quand une animation est
 * refaite, elle garde son nom et son cadre, et rien ne change ici. En mouvement réduit, le champion
 * ne danse pas : il est content, fixe.
 */
import abouCongrat10 from "../../../../assets/animations/abou/abou-congrat10.webp"
import samCongrat2 from "../../../../assets/animations/sam/sam-congrat2.webp"
import tinaCongrat10 from "../../../../assets/animations/tina/tina-congrat10.webp"
import tinaCongrat7 from "../../../../assets/animations/tina/tina-congrat7.webp"
import zoeCongrat4 from "../../../../assets/animations/zoe/zoe-congrat4.webp"
import zoeCongrat5 from "../../../../assets/animations/zoe/zoe-congrat5.webp"
import abouHappy from "../../../../assets/champions/abou_happy.png"
import abouMain from "../../../../assets/champions/abou_main.png"
import samHappy from "../../../../assets/champions/sam_happy.png"
import samMain from "../../../../assets/champions/sam_main.png"
import tinaHappy from "../../../../assets/champions/tina_happy.png"
import tinaMain from "../../../../assets/champions/tina_main.png"
import zoeHappy from "../../../../assets/champions/zoe_happy.png"
import zoeMain from "../../../../assets/champions/zoe_main.png"
import { type ChampionName } from "../Avatar/ChampionAvatar"

/** `main`, `happy` quand il célèbre, `dancing` quand il danse ce que l'élève vient de finir. */
export type ChampionPose = "main" | "happy" | "dancing"

const FIGURES: Record<ChampionName, Record<"main" | "happy", number>> = {
    sam: { main: samMain, happy: samHappy },
    zoe: { main: zoeMain, happy: zoeHappy },
    tina: { main: tinaMain, happy: tinaHappy },
    abou: { main: abouMain, happy: abouHappy },
}

const DANCES: Record<ChampionName, number[]> = {
    sam: [samCongrat2],
    zoe: [zoeCongrat4, zoeCongrat5],
    tina: [tinaCongrat7, tinaCongrat10],
    abou: [abouCongrat10],
}

/** La toile commune des poses fixes : 512 × 853. */
export const FIGURE_RATIO = 512 / 853

/**
 * La toile des danses : 300 px de haut, et la largeur de l'ancienne animation (462 à 532 px). La
 * plus large fait la boîte ; l'image s'y pose entière (`contain`).
 */
export const DANCE_RATIO = 532 / 300

/** Une des danses du champion. `random` est injecté pour le test. */
export function pickDance(name: ChampionName, random: () => number = Math.random): number {
    const dances = DANCES[name]
    return dances[Math.min(dances.length - 1, Math.floor(random() * dances.length))]
}

/** La danse tirée pour ce champion : la même tant qu'il ne change pas, une des siennes s'il change. */
export function danceOf(
    drawn: { name: ChampionName; dance: number } | null,
    name: ChampionName,
    random: () => number = Math.random
): { name: ChampionName; dance: number } {
    return drawn != null && drawn.name === name ? drawn : { name, dance: pickDance(name, random) }
}

/** L'image à montrer, et si elle bouge. `dance` : la danse déjà tirée (défaut : une au hasard). */
export function championFigureSource(
    name: ChampionName,
    pose: ChampionPose,
    reduceMotion: boolean,
    dance: number = pickDance(name)
): { source: number; animated: boolean } {
    if (pose === "dancing" && !reduceMotion) return { source: dance, animated: true }
    return { source: FIGURES[name][pose === "dancing" ? "happy" : pose], animated: false }
}
